import { RESOURCE_SECTION } from 'constants/constants';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useUser from '../../hooks/userHooks';
import { get } from '../../utils/localStorage';
import { hasRecruitAccess, hasRetainAccess, storeRedirectUrl } from '../../utils/redirectUtils';
import { isEmpty as isStringEmpty } from '../../utils/string';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  redirectPath?: string;
  requireAccessTo?: keyof typeof RESOURCE_SECTION;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  allowedRoles = [],
  redirectPath = '/',
  requireAccessTo,
}) => {
  const location = useLocation();
  const userRole = get('userRole');
  const { user, isLoading } = useUser();

  // Check if user is authenticated
  const token = get('token') as string;
  const refreshToken = get('refreshToken') as string;

  if (isStringEmpty(token) && isStringEmpty(refreshToken)) {
    // Store the full URL (including query params and hash) for post-auth redirect
    const fullUrl = `${location.pathname}${location.search}${location.hash}`;
    storeRedirectUrl(fullUrl);

    return <Navigate to={redirectPath} replace state={{ from: fullUrl }} />;
  }

  if (isLoading) {
    return null;
  }

  // If user data failed to load, redirect to login
  if (!user) {
    return <Navigate to="/employee-login" replace state={{ from: location.pathname }} />;
  }

  const userForPermissions = {
    role: userRole,
    configuration: user?.configuration || {},
  };

  // Check role-based access if specified
  if (allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some(role => userRole === role);

    if (!hasRequiredRole) {
      // For role-based restrictions, redirect to appropriate page without storing URL
      // as this is a permission issue, not an authentication issue
      return <Navigate to={redirectPath} replace state={{ from: location.pathname }} />;
    }
  }

  // Check access-based permissions if specified
  if (requireAccessTo) {
    let hasAccess = false;

    switch (requireAccessTo) {
      case RESOURCE_SECTION.RECRUIT:
        hasAccess = hasRecruitAccess(userForPermissions);
        break;
      case RESOURCE_SECTION.RETAIN:
        hasAccess = hasRetainAccess(userForPermissions);
        break;
      case RESOURCE_SECTION.ALL:
        hasAccess = hasRecruitAccess(userForPermissions) || hasRetainAccess(userForPermissions);
        break;
    }

    if (!hasAccess) {
      return <Navigate to={redirectPath} replace state={{ from: location.pathname }} />;
    }
  }

  return <>{children}</>;
};

export default RoleBasedRoute;
